<template>
  <h2>{{ t('payments') }}</h2>
  <div v-if="payments" class="relative mt-4">
    <div
      class="grid grid-cols-1 gap-2"
      :class="{
        'md:grid-cols-[repeat(8,minmax(max-content,1fr))]': showAllocatedAmounts,
        'md:grid-cols-[repeat(6,minmax(max-content,1fr))]': !showAllocatedAmounts,
      }"
    >
      <PaymentListItem
        v-for="payment in payments"
        :key="payment.id"
        :payment="payment"
        :show-allocated-amounts="showAllocatedAmounts"
        :invoice-allocated-amount="invoiceAllocatedAmounts?.get(payment.id)"
        :booking-allocated-amount="getBookingShareAmount(payment, booking?.id)"
        :to="
          getBookingPaymentDestination(payment, booking?.id, {
            canOpenPayment: $can('get', 'Payment'),
          })
        "
        @click="openPaymentDetails(payment)"
      />
    </div>
    <div v-if="payments.length === 0" class="rounded-lg border border-obsidian-200 bg-white p-4">
      {{ hasInvoice ? t('noPaymentsInvoice') : t('noPayments') }}
    </div>

    <div v-if="invoiceNettings.length" class="mt-6">
      <h3 class="mb-2 text-sm font-semibold uppercase text-obsidian-600">
        {{ t('nettings') }}
      </h3>
      <div class="grid grid-cols-1 gap-2">
        <div
          v-for="netting in invoiceNettings"
          :key="netting.nettingId"
          class="grid w-full grid-cols-2 items-start gap-x-4 gap-y-2 rounded-md bg-white p-4 text-left md:grid-cols-[max-content_max-content_1fr] md:gap-6 md:gap-y-0"
        >
          <div>
            <span v-currency="netting.amount" class="font-bold tabular-nums" />
          </div>
          <div class="flex items-center gap-2">
            {{ t(`nettingTypes.${netting.type}`) }}
            <span class="font-mono text-xs text-obsidian-400">{{
              netting.nettingId.slice(0, 8)
            }}</span>
          </div>
          <div class="min-w-0 break-words text-obsidian-500">
            {{ netting.description || '-' }}
          </div>
        </div>
      </div>
    </div>

    <Modal v-model="showApplyCoupon" show-close>
      <template #title>{{ t('applyCoupon') }}</template>
      <ApplyCoupon
        v-if="booking"
        :booking-id="booking.id"
        @cancel="showApplyCoupon = false"
        @success="showApplyCoupon = false"
      />
    </Modal>

    <Modal v-model="showStoredPaymentMethods" show-close>
      <template #title>{{ t('storedPaymentMethods') }}</template>
      <StoredPaymentMethodsModal v-if="booking" :booking="booking" />
    </Modal>

    <Modal v-model="showAddLedgerEntriesPayment" show-close>
      <template #title>{{ t('openReceivables') }}</template>
      <LedgerEntriesPayments
        v-if="booking"
        :booking="booking"
        :payments="payments"
        @on-cancel="showAddLedgerEntriesPayment = false"
        @on-submit="
          (value) => {
            chargeAmount(value);
            showAddLedgerEntriesPayment = false;
          }
        "
      />
    </Modal>

    <Modal v-model="showRefundLedgerEntriesPayment" show-close>
      <template #title>{{ t('openRefund') }}</template>
      <LedgerEntriesRefund
        v-if="booking"
        :booking="booking"
        :payments="payments"
        @success="showRefundLedgerEntriesPayment = false"
        @cancel="showRefundLedgerEntriesPayment = false"
      />
    </Modal>

    <Modal v-model="showWriteOff" show-close>
      <template #title>{{ t('writeOff') }}</template>
      <WriteOff
        v-if="
          booking &&
          $can('writeOff', booking) &&
          (openBalance.hasOpenBalance || openBalance.hasOpenRefunds)
        "
        :booking="booking"
        @success="showWriteOff = false"
        @cancel="showWriteOff = false"
      />
    </Modal>

    <Modal v-model="showManualLaneCredit" show-close>
      <template #title>{{ t('manualLaneCredit') }}</template>
      <ManualLaneCredit
        v-if="booking"
        :booking="booking"
        @success="showManualLaneCredit = false"
        @cancel="showManualLaneCredit = false"
      />
    </Modal>

    <PaymentsForm
      v-if="addingPayment"
      :booking-id="booking?.id"
      :initial-invoice-id="invoice?.id"
      :initial-direction="invoice?.type === InvoiceType.SELF_BILLING ? 'credit' : 'payment'"
      :selectable-invoices="selectableInvoices"
      @submit="savePayment"
      @cancel="addingPayment = false"
    />

    <div v-else class="mt-5 flex items-start justify-between gap-4 max-sm:flex-col">
      <div class="flex flex-wrap items-center gap-2">
        <div v-if="booking?.bookingIpInfo?.summary" class="rounded-sm bg-white/60 px-4 py-0.5">
          {{ booking?.bookingIpInfo.summary }}
        </div>
        <Information v-if="!isNil(booking?.fraudDetectionInfo?.fraudProbability)">
          <template #trigger>
            <Badge :variant="fraudProbabilityVariant" size="md">
              {{ t('fraudProbability') }}:
              {{ formatFraudProbability(booking.fraudDetectionInfo.fraudProbability) }}
            </Badge>
          </template>
          <template #modal>
            {{ t('fraudProbabilityTooltip', booking.fraudDetectionInfo) }}
          </template>
        </Information>
      </div>
      <div class="grid w-full items-center gap-2 md:flex md:justify-end md:gap-4">
        <CVButton
          v-if="
            $can('read', 'LedgerEntry') &&
            (openBalance.hasOpenBalance || booking?.hasPeriodicInvoicing)
          "
          @click.prevent="showAddLedgerEntriesPayment = true"
          >{{ t('addLedgerEntriesPayment') }}</CVButton
        >
        <CVButton
          v-if="$can('read', 'LedgerEntry') && openBalance.hasOpenRefunds"
          @click.prevent="showRefundLedgerEntriesPayment = true"
          >{{ t('addLedgerEntriesRefund') }}</CVButton
        >
        <DropdownDotted
          :buttons="[
            ...($can('create', 'Payment')
              ? [
                  {
                    title: t('addPayment'),
                    onClick: () => (addingPayment = true),
                  },
                ]
              : []),
            ...(booking &&
            $can('writeOff', booking) &&
            (openBalance.hasOpenBalance || openBalance.hasOpenRefunds)
              ? [
                  {
                    title: t('writeOff'),
                    onClick: () => (showWriteOff = true),
                  },
                ]
              : []),
            ...(booking && $can('refund', 'LedgerEntry')
              ? [
                  {
                    title: t('manualLaneCredit'),
                    onClick: () => (showManualLaneCredit = true),
                  },
                ]
              : []),
            ...(booking && $can('get', 'Coupon') && $can('update', subject('Booking', booking))
              ? [
                  {
                    title: t('applyCoupon'),
                    onClick: () => (showApplyCoupon = true),
                  },
                ]
              : []),
            ...(booking &&
            !invoice &&
            booking.status !== BookingStatus.FINALIZED &&
            $can('charge', booking)
              ? [
                  {
                    title: t('storedPaymentMethods'),
                    onClick: () => (showStoredPaymentMethods = true),
                  },
                ]
              : []),
          ]"
          data-testid="payments-dropdown"
        />
      </div>
    </div>
    <div v-if="booking?.flags?.createdPaymentLinkDate" class="mt-4">
      <p>
        {{ t('createPaymentLinkAt') }}
        {{ formatDate(booking.flags.createdPaymentLinkDate, dateFormat) }}
      </p>
    </div>
    <div
      v-if="isCreating || isChargingAmount || isAllocating"
      class="absolute inset-[-10px] flex items-center justify-center bg-black bg-opacity-30"
    >
      <Spinner />
    </div>
  </div>

  <div v-else class="flex h-20 items-center justify-center">
    <Spinner />
  </div>

  <PaymentDetailsModal
    v-if="selectedPayment"
    :key="selectedPayment.id"
    v-model="showPaymentDetailsModal"
    :payment="selectedPayment"
    :booking-id="booking?.id"
    :invoice="invoice"
  />
</template>

<script lang="ts" setup>
import { FindAllPaymentsSort } from '@/entities/find-all-payments-sort.enum';
import { Order } from '@/entities/pagination/order.enum';
import type { Payment } from '@/entities/payments/payment.entity';
import PaymentListItem from './PaymentListItem.vue';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { useI18n } from 'vue-i18n';
import { useCreatePayment, usePaymentsWithQuery } from '@/queries/use-payments';
import { computed, ref } from 'vue';
import PaymentsForm from './PaymentsForm.vue';
import { useChargeAmount } from '@/queries/use-payment-api';
import type { Booking } from '@/entities/bookings/booking.entity';
import PaymentDetailsModal from './PaymentDetailsModal.vue';
import { showPaymentLinkToCopy } from '@/utils/select-payment-link-receiver';
import {
  type ChargeAmountDto,
  FindAllLedgerEntriesSort,
  SendPaymentLinkResult,
} from '@carvia/ros-client-types';
import type { Invoice } from '@/entities/invoices/invoice.entity.ts';
import { InvoiceType } from '@/entities/invoices/invoice-type.enum';
import LedgerEntriesPayments from './LedgerEntriesPayments.vue';
import Modal from '../Modal.vue';
import { formatDate } from '@/hooks/use-formatted-date';
import { useDateFormat } from '@/hooks/use-date-format';
import LedgerEntriesRefund from './LedgerEntriesRefund.vue';
import { useAllocatePayment, useLedgerEntries } from '@/queries/use-ledger.ts';
import { Alert } from '@/services/alert.service';
import type { PaymentFormSubmitPayload } from '@/components/booking-forms/PaymentsForm.vue';
import WriteOff from './WriteOff.vue';
import { useOpenBalance } from '@/hooks/use-open-balance';
import { useAbility } from '@casl/vue';
import DropdownDotted from '../DropdownDotted.vue';
import ManualLaneCredit from './ManualLaneCredit.vue';
import { sumBy, isNil } from 'lodash';
import Badge from '@/components/Badge.vue';
import type { BadgeVariant } from '@carvia/theme/lib/cva/badge';
import Information from '../Information.vue';
import ApplyCoupon from './ApplyCoupon.vue';
import StoredPaymentMethodsModal from './StoredPaymentMethodsModal.vue';
import { subject } from '@casl/ability';
import { BookingStatus } from '@/entities/bookings/booking-status.enum';
import { getBookingPaymentDestination, getBookingShareAmount } from '@/utils/booking-payment-share';

const props = defineProps<{
  booking?: Booking | null;
  invoice?: Invoice;
  initialDirection?: 'payment' | 'credit';
  selectableInvoices?: Invoice[];
}>();

const emit = defineEmits<{
  (e: 'onChanged'): void;
}>();

const { t } = useI18n();
const ability = useAbility();
const dateFormat = useDateFormat();

const showAllocatedAmounts = computed(() => ability.can('list', 'PaymentAllocation'));

const addingPayment = ref(false);
const showWriteOff = ref(false);
const showManualLaneCredit = ref(false);
const selectedPayment = ref<Payment | null>(null);
const showPaymentDetailsModal = ref(false);
const showAddLedgerEntriesPayment = ref(false);
const showRefundLedgerEntriesPayment = ref(false);
const showApplyCoupon = ref(false);
const showStoredPaymentMethods = ref(false);

const formatFraudProbability = (probability: number) => {
  return `${(probability * 100).toFixed(1)}%`;
};

const fraudProbabilityVariant = computed((): BadgeVariant => {
  const probability = props.booking?.fraudDetectionInfo?.fraudProbability;
  if (probability == null) return 'neutral';
  if (probability >= 0.7) return 'problem';
  if (probability >= 0.4) return 'warning';
  return 'success';
});

const params = computed(() => ({
  bookingIds: props.booking ? [props.booking.id] : undefined,
  invoiceIds: props.invoice ? [props.invoice.id] : undefined,
  limit: 100,
  sort: FindAllPaymentsSort.CREATED_DATE,
  order: Order.ASC,
}));

const hasInvoice = computed(() => !!props.invoice);

const { data: ledgerEntriesData } = useLedgerEntries(
  computed(() => ({
    invoiceIds: props.invoice ? [props.invoice.id] : [],
    sort: [FindAllLedgerEntriesSort.DUE_DATE],
    order: Order.ASC,
    limit: 999,
  })),
  hasInvoice,
);

const ledgerEntries = useFlattenPaginatedData(ledgerEntriesData);

const { data: paymentsData, refetch: refetchPayments } = usePaymentsWithQuery(params);
const { mutateAsync: createPayment, isPending: isCreating } = useCreatePayment();
const { mutateAsync: charge, isPending: isChargingAmount } = useChargeAmount();
const { mutateAsync: allocatePayment, isPending: isAllocating } = useAllocatePayment();
const flattenedPayments = useFlattenPaginatedData(paymentsData);

interface InvoiceNettingSummary {
  nettingId: string;
  type: string;
  description: string | null;
  amount: number;
}

const invoicePaymentsData = computed(() => {
  if (!hasInvoice.value) {
    return { payments: flattenedPayments.value, nettings: [] as InvoiceNettingSummary[] };
  }
  const paymentMap = new Map<string, Payment>();
  const invoiceAllocatedAmounts = new Map<string, number>();
  const nettingMap = new Map<string, InvoiceNettingSummary>();
  for (const payment of flattenedPayments.value) {
    paymentMap.set(payment.id, payment);
    invoiceAllocatedAmounts.set(payment.id, 0);
  }
  for (const ledgerEntry of ledgerEntries.value) {
    const ledgerEntryInvoicedRatio =
      ledgerEntry.amount === 0
        ? 1
        : sumBy(
            ledgerEntry.invoiceAllocations.filter(
              ({ invoice }) => invoice.id === props.invoice?.id,
            ),
            'amount',
          ) / ledgerEntry.amount;
    for (const paymentAllocation of ledgerEntry.paymentAllocations) {
      const paymentId = paymentAllocation.payment.id;
      invoiceAllocatedAmounts.set(
        paymentId,
        (invoiceAllocatedAmounts.get(paymentId) ?? 0) +
          (paymentAllocation.amount / 100) * ledgerEntryInvoicedRatio,
      );

      // Prefer the /payments row (has totalAllocatedAmount); only insert ledger-nested payments when missing.
      paymentMap.set(paymentId, paymentMap.get(paymentId) ?? paymentAllocation.payment);
    }
    for (const nettingAllocation of ledgerEntry.nettingAllocations ?? []) {
      const nettingId = nettingAllocation.netting?.id;
      if (!nettingId) continue;
      const existing = nettingMap.get(nettingId);
      const attributedAmount = (nettingAllocation.amount / 100) * ledgerEntryInvoicedRatio;
      if (existing) {
        existing.amount += attributedAmount;
      } else {
        nettingMap.set(nettingId, {
          nettingId,
          type: nettingAllocation.netting.type,
          description: nettingAllocation.netting.description,
          amount: attributedAmount,
        });
      }
    }
  }
  return {
    payments: Array.from(paymentMap.values()).sort((a, b) => (a.date > b.date ? -1 : 1)),
    invoicePaymentAllocations: invoiceAllocatedAmounts,
    nettings: Array.from(nettingMap.values()).filter((n) => Math.abs(n.amount) > 0.005),
  };
});
const payments = computed(() => invoicePaymentsData.value.payments);
const invoiceAllocatedAmounts = computed(() => invoicePaymentsData.value.invoicePaymentAllocations);
const invoiceNettings = computed(() => invoicePaymentsData.value.nettings ?? []);

const savePayment = async (payload: PaymentFormSubmitPayload) => {
  const { allocation, ...createDto } = payload;
  const payment = await createPayment(createDto);
  if (payment && allocation?.scope) {
    try {
      await allocatePayment({
        paymentId: payment.id,
        scope: allocation.scope,
      });
    } catch {
      Alert.fire({
        icon: 'warning',
        titleText: t('allocationFailed'),
        text: t('allocationFailedText'),
      });
    }
  }
  addingPayment.value = false;
  emit('onChanged');
};

const chargeAmount = async (chargeAmountDto: ChargeAmountDto) => {
  showAddLedgerEntriesPayment.value = false;
  emit('onChanged');
  const { paymentLink } = await charge(chargeAmountDto);
  if (paymentLink?.sendPaymentLinkResult === SendPaymentLinkResult.DISABLED) {
    showPaymentLinkToCopy(paymentLink.url);
  }
  refetchPayments();
};

const openPaymentDetails = (payment: Payment) => {
  if (
    getBookingPaymentDestination(payment, props.booking?.id, {
      canOpenPayment: ability.can('get', 'Payment'),
    })
  )
    return;
  selectedPayment.value = payment;
  showPaymentDetailsModal.value = true;
};

const openBalance = useOpenBalance(() => props.booking?.currentBalance);
</script>

<i18n lang="json">
{
  "en": {
    "payments": "Payments",
    "addPayment": "Enter Payment",
    "noPayments": "This booking has no payments",
    "noPaymentsInvoice": "This invoice has no payments",
    "createPaymentLinkAt": "Created last payment link at:",
    "addLedgerEntriesPayment": "Request payment",
    "openReceivables": "Open Receivables",
    "addLedgerEntriesRefund": "Refund open credit",
    "openRefund": "Refund credit",
    "allocationFailed": "Allocation failed",
    "allocationFailedText": "The payment was created but could not be allocated. You can allocate it manually.",
    "writeOff": "Write off amount",
    "manualLaneCredit": "Credit charge",
    "fraudProbability": "Fraud Risk",
    "fraudProbabilityTooltip": "Fraud probability score from the fraud detection API. Higher values indicate higher risk. Failed checks: {failedChecks}. Final decision: {finalDecision}",
    "applyCoupon": "Apply coupon",
    "storedPaymentMethods": "Stored payment methods",
    "nettings": "Nettings"
  },
  "de": {
    "payments": "Zahlungen",
    "addPayment": "Zahlung eintragen",
    "noPayments": "Diese Buchung hat noch keine Zahlungen",
    "noPaymentsInvoice": "Diese Rechnung hat noch keine Zahlungen",
    "createPaymentLinkAt": "Letzter Zahlungslink erstellt am:",
    "addLedgerEntriesPayment": "Zahlung anfordern",
    "openReceivables": "Offene Forderungen",
    "addLedgerEntriesRefund": "Offene Gutschriften erstatten",
    "openRefund": "Gutschriften erstatten",
    "allocationFailed": "Zuordnung fehlgeschlagen",
    "allocationFailedText": "Die Zahlung wurde erstellt, konnte aber nicht zugeordnet werden. Sie können sie manuell zuordnen.",
    "writeOff": "Betrag abschreiben",
    "manualLaneCredit": "Posten gutschreiben",
    "fraudProbability": "Betrugsrisiko",
    "fraudProbabilityTooltip": "Betrugswahrscheinlichkeit aus der Betrugserkennung. Höhere Werte bedeuten höheres Risiko. Fehlgeschlagene Prüfungen: {failedChecks}. Finale Entscheidung: {finalDecision}",
    "applyCoupon": "Gutschein einlösen",
    "storedPaymentMethods": "Hinterlegte Zahlungsmethoden",
    "nettings": "Verrechnungen"
  },
  "es": {
    "payments": "Pagos",
    "addPayment": "Registrar pago",
    "noPayments": "Esta reserva no tiene pagos",
    "noPaymentsInvoice": "Esta factura no tiene pagos",
    "createPaymentLinkAt": "Último enlace de pago creado el:",
    "addLedgerEntriesPayment": "Solicitar pago",
    "openReceivables": "Cobros pendientes",
    "addLedgerEntriesRefund": "Reembolsar crédito abierto",
    "openRefund": "Reembolsar crédito",
    "allocationFailed": "Fallo en la imputación",
    "allocationFailedText": "El pago se creó pero no pudo imputarse. Puede imputarlo manualmente.",
    "writeOff": "Dar de baja importe",
    "manualLaneCredit": "Abonar partida contable",
    "fraudProbability": "Riesgo de fraude",
    "fraudProbabilityTooltip": "Puntuación de probabilidad de fraude de la API antifraude. Valores más altos indican mayor riesgo. Comprobaciones fallidas: {failedChecks}. Decisión final: {finalDecision}",
    "applyCoupon": "Aplicar cupón",
    "storedPaymentMethods": "Métodos de pago guardados",
    "nettings": "Compensaciones"
  }
}
</i18n>
